// Lista paginada de relaciones de gastos para el rol revisor.
// Usa el patron usePaginatedList + PaginationControls del skill TINVAZ.
//
// Diferencia con MisRelacionesGastos: aqui se ven TODAS las relaciones
// (no solo las propias) para auditar y aprobar las que esten en BORRADOR.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Card, Table, Button, Badge, Alert, Form, Row, Col, Spinner } from 'react-bootstrap';
import { FaFileInvoiceDollar, FaEye, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import PaginationControls from '../commons/PaginationControls';
import { filterData } from '../../api/common';

const formatoCOP = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;

const ESTADO_BADGE = {
  BORRADOR: 'warning',
  APROBADA: 'info',
  SINCRONIZANDO: 'info',
  SINCRONIZADA: 'success',
  SYNC_ERROR: 'danger',
  CANCELADA: 'secondary'
};

const ESTADOS = ['BORRADOR', 'APROBADA', 'SINCRONIZANDO', 'SINCRONIZADA', 'SYNC_ERROR', 'CANCELADA'];

const RevisionRelaciones = () => {
  const navigate = useNavigate();
  const [responsables, setResponsables] = useState([]);

  const {
    results,
    pagination,
    loading,
    error,
    filters,
    updateFilter,
    resetFilters,
    changePage,
    changePerPage,
    refresh,
    pendingUpdates
  } = usePaginatedList({
    endpoint: '/RelacionGastos/paginar',
    initialPerPage: 20,
    sort: { createdAt: -1 },
    buildConditions: (f) => {
      const cond = { activo: true };
      if (f.estado) cond.estado = f.estado;
      if (f.responsable) cond.responsable = f.responsable;
      return cond;
    }
  });

  // Cargar lista de responsables (usuarios activos no clientes) para el filtro
  useEffect(() => {
    (async () => {
      try {
        const resp = await filterData('usuarios', [
          { $match: { status: 'activo' } },
          { $lookup: { from: 'roles', localField: 'role', foreignField: '_id', as: 'result' } },
          { $match: { 'result.nombre': { $ne: 'Cliente' } } }
        ]);
        const lista = (resp.data.data || []).map(u => ({
          _id: u._id,
          label: `${u.nombre || ''} ${u.apellido || ''}`.trim()
        }));
        setResponsables(lista);
      } catch (err) {
        console.error('Error cargando responsables:', err);
      }
    })();
  }, []);

  // Una relacion esta "sincronizada" cuando estado === 'SINCRONIZADA'.
  // Las demas combinaciones (BORRADOR/APROBADA/SYNC_ERROR/etc) se consideran NO sincronizadas.
  const isSync = (r) => r.estado === 'SINCRONIZADA';

  return (
    <Container fluid className="py-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h2 className="text-primary fw-bold mb-0">
            <FaFileInvoiceDollar className="me-2" />
            Revisión de Relaciones
          </h2>
          <small className="text-muted">Auditoría y aprobación de cierres del módulo financiero.</small>
        </Col>
      </Row>

      {/* Filtros */}
      <Card className="mb-3 shadow-sm w-100">
        <Card.Body className='w-100'>
          <Row className="g-2">
            <Col md={3}>
              <Form.Label className="small mb-1">Estado</Form.Label>
              <Form.Select
                size="sm"
                value={filters.estado || ''}
                onChange={(e) => updateFilter('estado', e.target.value)}
              >
                <option value="">Todos</option>
                {ESTADOS.map(s => <option key={s} value={s}>{s}</option>)}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label className="small mb-1">Responsable</Form.Label>
              <Form.Select
                size="sm"
                value={filters.responsable || ''}
                onChange={(e) => updateFilter('responsable', e.target.value)}
              >
                <option value="">Todos</option>
                {responsables.map(u => (
                  <option key={u._id} value={u._id}>{u.label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button variant="outline-secondary" size="sm" onClick={resetFilters}>
                Limpiar
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Error */}
      {error && (
        <Alert variant="danger" className="d-flex align-items-center justify-content-between">
          <span><strong>Error al cargar datos:</strong> {error}</span>
          <Button variant="outline-danger" size="sm" onClick={refresh}>Reintentar</Button>
        </Alert>
      )}

      {/* Tabla */}
      <Card className="shadow-sm w-100">
        <Card.Body className="p-0 w-100">
          <div className="table-responsive">
            <Table striped bordered hover responsive size="sm" className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Consecutivo</th>
                  <th>Estado</th>
                  <th>Fecha</th>
                  <th>Responsable</th>
                  <th className="text-end">Monto gastos</th>
                  <th className="text-center">SYNC</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      <Spinner animation="border" size="sm" /> Cargando...
                    </td>
                  </tr>
                ) : results.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      <Alert variant="info" className="mb-0 d-inline-flex flex-column align-items-center w-100">
                        <i className="bi bi-inbox fs-1 d-block mb-2" />
                        <p className="mb-2">No se encontraron relaciones con los filtros seleccionados.</p>
                        <Button variant="outline-info" size="sm" onClick={resetFilters}>Limpiar filtros</Button>
                      </Alert>
                    </td>
                  </tr>
                ) : (
                  results.map((r) => (
                    <tr key={r._id}>
                      <td><code>{r.consecutivo}</code></td>
                      <td>
                        <Badge bg={ESTADO_BADGE[r.estado] || 'secondary'}>{r.estado}</Badge>
                      </td>
                      <td><small>{r.createdAt ? new Date(r.createdAt).toLocaleDateString('es-CO') : '—'}</small></td>
                      <td><small>{r.responsable?.nombre || ''} {r.responsable?.apellido || ''}</small></td>
                      <td className="text-end fw-bold">{formatoCOP(r.montoTotalGastos)}</td>
                      <td className="text-center">
                        {isSync(r)
                          ? <FaCheckCircle className="text-success" size={18} title="Sincronizado con ERP" />
                          : <FaTimesCircle className="text-danger" size={18} title="No sincronizado" />}
                      </td>
                      <td className="text-center">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => navigate(`/RevisionRelaciones/${r._id}`)}
                          title="Ver detalle"
                        >
                          <FaEye />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Paginacion */}
      <div className="mt-3">
        <PaginationControls
          {...pagination}
          onPageChange={changePage}
          onPerPageChange={changePerPage}
          onRefresh={refresh}
          loading={loading}
          pendingUpdates={pendingUpdates}
        />
      </div>
    </Container>
  );
};

export default RevisionRelaciones;
